import { useTranslation } from 'react-i18next'

import { BannerContentDisplay } from '../display/BannerContentDisplay'
import { ColorSwatchGrid } from '../display/ColorSwatchGrid'
import { ConfigSection } from '../display/ConfigSection'
import { EditableConfigRow } from '../display/EditableConfigRow'
import type { ConfigQueryResult } from '../PartnerConfig.types'
import { objectKeys } from '../utils'

import { Accordion, Checkbox, Flex, FormField, TextInput } from '@percent/lemonade'
import type { GivingHomePageBannerContentConfigValue, GivingThemeConfigValue } from '@percent/admin-dashboard/api/types'
import { isValidHex, useBrandingSection } from './branding/useBrandingSection'

type BrandingSectionProps = {
  theme: ConfigQueryResult<GivingThemeConfigValue>
  bannerContent: ConfigQueryResult<GivingHomePageBannerContentConfigValue>
  isSaving: boolean
  savingKey: string | null
  mutationError?: string
  resetSaving: () => void
  saveTheme: (value: GivingThemeConfigValue, onSuccess?: () => void) => void
  saveBannerContent: (value: GivingHomePageBannerContentConfigValue, onSuccess?: () => void) => void
}

export function BrandingSection({
  theme,
  bannerContent,
  isSaving,
  savingKey,
  mutationError,
  resetSaving,
  saveTheme,
  saveBannerContent
}: BrandingSectionProps) {
  const { t } = useTranslation()

  const {
    editingKey,
    startEditing,
    cancel,
    errorForKey,
    themeDraft,
    setThemeDraft,
    overlayInput,
    handleOverlayChange,
    overlayValid,
    bannerContentDraft,
    setBannerContentDraft,
    themeIsValid
  } = useBrandingSection({
    themeData: theme.data,
    bannerContentData: bannerContent.data,
    savingKey,
    isSaving,
    mutationError,
    resetSaving
  })

  const welcomeCopyColorInvalid =
    !!bannerContentDraft.welcomeCopyColor && !isValidHex(bannerContentDraft.welcomeCopyColor)

  return (
    <ConfigSection title={t('partnerConfig.section.branding')}>
      <EditableConfigRow
        id="branding.theme"
        label={t('partnerConfig.branding.theme')}
        isLoading={theme.isLoading}
        error={theme.error}
        isEditing={editingKey === 'theme'}
        isSaving={savingKey === 'theme' && isSaving}
        saveDisabled={isSaving || !themeIsValid}
        saveError={errorForKey('theme')}
        onEdit={() => startEditing('theme')}
        onCancel={cancel}
        onSave={() => saveTheme(themeDraft, cancel)}
        displayContent={
          theme.data !== undefined ? (
            <Accordion defaultValue="theme">
              <Accordion.Item value="theme">
                <Accordion.Control>{t('partnerConfig.branding.themeColours')}</Accordion.Control>
                <Accordion.Panel>
                  <ColorSwatchGrid
                    colours={theme.data.colours}
                    overlayGradientPercentage={theme.data.overlayGradientPercentage}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ) : undefined
        }
        editContent={
          <Flex direction="column" gap={2}>
            {objectKeys(themeDraft.colours).map(colourKey => {
              const val = themeDraft.colours[colourKey]
              const invalid = !isValidHex(val)

              return (
                <FormField
                  key={colourKey}
                  label={t(`partnerConfig.branding.colour.${colourKey}`)}
                  status={invalid ? 'danger' : 'default'}
                  statusMessage={invalid ? t('partnerConfig.branding.edit.invalidHex') : undefined}
                >
                  <TextInput
                    name={colourKey}
                    value={val}
                    status={invalid ? 'danger' : 'default'}
                    onChange={event =>
                      setThemeDraft(prev => ({
                        ...prev,
                        colours: { ...prev.colours, [colourKey]: event.target.value }
                      }))
                    }
                    placeholder={t('partnerConfig.branding.edit.colourPlaceholder')}
                  />
                </FormField>
              )
            })}
            <FormField
              label={t('partnerConfig.branding.edit.overlayGradientLabel')}
              status={overlayValid ? 'default' : 'danger'}
              statusMessage={overlayValid ? undefined : t('partnerConfig.branding.edit.invalidOverlayGradient')}
            >
              <TextInput
                name="overlayGradientPercentage"
                type="number"
                min="0"
                max="100"
                value={overlayInput}
                status={overlayValid ? 'default' : 'danger'}
                onChange={event => handleOverlayChange(event.target.value)}
                placeholder={t('partnerConfig.branding.edit.overlayGradientPlaceholder')}
              />
            </FormField>
          </Flex>
        }
      />

      <EditableConfigRow
        id="branding.bannerContent"
        label={t('partnerConfig.branding.bannerContent')}
        isLoading={bannerContent.isLoading}
        error={bannerContent.error}
        isEditing={editingKey === 'bannerContent'}
        isSaving={savingKey === 'home_page_banner_content' && isSaving}
        saveError={errorForKey('home_page_banner_content')}
        onEdit={() => startEditing('bannerContent')}
        onCancel={cancel}
        saveDisabled={isSaving || welcomeCopyColorInvalid}
        onSave={() => saveBannerContent(bannerContentDraft, cancel)}
        displayContent={
          bannerContent.data !== undefined ? <BannerContentDisplay data={bannerContent.data} /> : undefined
        }
        editContent={
          <Flex direction="column" gap={4}>
            <Checkbox
              name="welcomeCopy"
              label={t('partnerConfig.branding.welcomeCopy')}
              active={bannerContentDraft.welcomeCopy}
              variant="default"
              onChange={event => setBannerContentDraft(prev => ({ ...prev, welcomeCopy: event.currentTarget.checked }))}
            />
            <Checkbox
              name="searchBar"
              label={t('partnerConfig.branding.searchBar')}
              active={bannerContentDraft.searchBar}
              variant="default"
              onChange={event => setBannerContentDraft(prev => ({ ...prev, searchBar: event.currentTarget.checked }))}
            />
            <FormField label={t('partnerConfig.branding.edit.welcomeCopyTitle')}>
              <TextInput
                name="welcomeCopyTitle"
                value={bannerContentDraft.welcomeCopyTitle ?? ''}
                onChange={event =>
                  setBannerContentDraft(prev => ({ ...prev, welcomeCopyTitle: event.target.value || undefined }))
                }
                placeholder={t('partnerConfig.branding.edit.titlePlaceholder')}
              />
            </FormField>
            <FormField label={t('partnerConfig.branding.edit.welcomeCopySubtitle')}>
              <TextInput
                name="welcomeCopySubtitle"
                value={bannerContentDraft.welcomeCopySubtitle ?? ''}
                onChange={event =>
                  setBannerContentDraft(prev => ({ ...prev, welcomeCopySubtitle: event.target.value || undefined }))
                }
                placeholder={t('partnerConfig.branding.edit.subtitlePlaceholder')}
              />
            </FormField>
            <FormField
              label={t('partnerConfig.branding.copyColor')}
              status={welcomeCopyColorInvalid ? 'danger' : 'default'}
              statusMessage={welcomeCopyColorInvalid ? t('partnerConfig.branding.edit.invalidHex') : undefined}
            >
              <TextInput
                name="welcomeCopyColor"
                value={bannerContentDraft.welcomeCopyColor ?? ''}
                status={welcomeCopyColorInvalid ? 'danger' : 'default'}
                onChange={event =>
                  setBannerContentDraft(prev => ({ ...prev, welcomeCopyColor: event.target.value || undefined }))
                }
                placeholder={t('partnerConfig.branding.copyColorPlaceholder')}
              />
            </FormField>
          </Flex>
        }
      />
    </ConfigSection>
  )
}
